// (B) Plain(커스텀) Menu 백엔드 — antd Menu 대체(standalone 네비/툴바/팝업 메뉴).
//   inline(MenuLeft 좌측 nav, 서브메뉴 inline 확장)·vertical(팝업 호스팅 메뉴)·horizontal(MenuTop/툴바, rc-overflow "더보기").
//   antd Menu v5 DOM 을 이중클래스로 미러링(각 요소에 `.om-menu-*` + `.om-menu-*` 동시 부여):
//     root ul: .om-menu .om-menu-root .om-menu-{inline|vertical|horizontal}(+ -overflow)
//     leaf li: .om-menu-item(+-selected/-active/-disabled/-danger) > [.om-menu-item-icon] .om-menu-title-content
//     divider li: .om-menu-item-divider · group li: .om-menu-item-group(>-group-title/-group-list)
//     submenu li: .om-menu-submenu(+-inline/-vertical/-horizontal/-open/-selected) > .om-menu-submenu-title(+.om-menu-submenu-arrow)
//       inline: 자식 ul .om-menu-sub .om-menu-inline 를 li 안에 렌더(openKeys 로 토글)
//       vertical/horizontal: 자식을 .om-menu-submenu-popup 팝업(Radix Popover)로 flyout
//     overflow(horizontal): 넘친 항목을 rest 서브메뉴로 접음 — li.om-menu-overflow-item(.om-menu-overflow-item-rest.om-menu-submenu)
//   → 기존 소비처/전역 SCSS(.om-menu-* 82줄/12파일)·JS(`.om-menu-submenu-open` blurHandler, getOverflowRestElement) 무변경 매칭.
//     rename 은 batch9 유예. 시각 base 는 styles/omMenu.css(:where 0-특이도, 소비처 SCSS 항상 우선).
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ForwardedRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import type { OmwMenuClickInfo, OmwMenuItem, OmwMenuProps, OmwMenuRef } from '../api/OmwMenu'
import { cx } from './cx'
import { useRovingKeydown } from './a11y/useRovingKeydown'
import '../styles/omwMenu.css'

type MenuMode = 'inline' | 'horizontal' | 'vertical'

// ── roving 키보드(inline/vertical 좌측 네비) ─────────────────────────────
// horizontal(rc-overflow) 은 hidden 측정 항목 포커스 문제로 범위 외 — 실사용(좌측 네비)만 우선 구현.
interface NavEntry {
  key: string
  disabled: boolean
  label: string
  kind: 'leaf' | 'submenu'
  open: boolean
}

// horizontal(roving 미적용)용 중립 ctx 핸들러 — 안정 참조.
const noopRegisterEl = (_key: string, _el: HTMLElement | null): void => {}
const noopSetActiveKey = (_key: string): void => {}

function menuTextLabel(item: OmwMenuItem): string {
  if (typeof item.ariaLabel === 'string' && item.ariaLabel) return item.ariaLabel
  if (typeof item.title === 'string') return item.title
  if (typeof item.label === 'string') return item.label
  return ''
}

/** 가시 항목을 로빙 순서로 평탄화(inline 열린 서브메뉴는 자식까지, 닫힘/flyout 은 타이틀만). */
function flattenNavigable(
  items: OmwMenuItem[],
  mode: MenuMode,
  openKeys: string[],
  out: NavEntry[] = [],
): NavEntry[] {
  for (const item of items) {
    if (item.type === 'divider') continue
    if (item.type === 'group') {
      if (item.children) flattenNavigable(item.children, mode, openKeys, out)
      continue
    }
    const key = item.key ?? ''
    if (!key) continue
    if (item.children != null) {
      const open = openKeys.includes(key)
      out.push({
        key,
        disabled: !!item.disabled,
        label: menuTextLabel(item),
        kind: 'submenu',
        open,
      })
      if (mode === 'inline' && open) flattenNavigable(item.children, mode, openKeys, out)
    } else {
      out.push({
        key,
        disabled: !!item.disabled,
        label: menuTextLabel(item),
        kind: 'leaf',
        open: false,
      })
    }
  }
  return out
}

function useMenuRoving(
  items: OmwMenuItem[],
  mode: MenuMode,
  openKeys: string[],
  onInlineToggle: (key: string) => void,
) {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const elMap = useRef(new Map<string, HTMLElement>())
  const nav = useMemo(() => flattenNavigable(items, mode, openKeys), [items, mode, openKeys])
  // Tab 진입점: 활성 항목(없으면 첫 활성 가능 항목)만 tabIndex 0.
  const anchorKey = activeKey ?? nav.find((n) => !n.disabled)?.key ?? null
  const activeIndex = nav.findIndex((n) => n.key === activeKey)

  const { onKeyDown } = useRovingKeydown({
    itemCount: nav.length,
    activeIndex,
    setActiveIndex: (i) => {
      const k = nav[i]?.key
      if (k == null) return
      setActiveKey(k)
      elMap.current.get(k)?.focus()
    },
    onActivate: (i) => {
      const k = nav[i]?.key
      if (k != null) elMap.current.get(k)?.click()
    },
    isDisabled: (i) => nav[i]?.disabled ?? false,
    getLabel: (i) => nav[i]?.label ?? '',
    orientation: mode === 'horizontal' ? 'horizontal' : 'vertical',
    // inline 서브메뉴: → 확장 / ← 축소(닫힘/열림 상태에 따라).
    onArrowRight:
      mode === 'inline'
        ? (i) => {
            const e = nav[i]
            if (e?.kind === 'submenu' && !e.open) {
              onInlineToggle(e.key)
              return true
            }
            return false
          }
        : undefined,
    onArrowLeft:
      mode === 'inline'
        ? (i) => {
            const e = nav[i]
            if (e?.kind === 'submenu' && e.open) {
              onInlineToggle(e.key)
              return true
            }
            return false
          }
        : undefined,
  })

  const registerEl = useCallback((key: string, el: HTMLElement | null) => {
    if (el) elMap.current.set(key, el)
    else elMap.current.delete(key)
  }, [])

  return { anchorKey, onKeyDown, registerEl, setActiveKey }
}

/** data-* 속성만 추려 DOM 으로 패스스루(EmailSelect data-value 등, antMenuItems.toAntItems 대응). */
function pickDataAttrs(item: OmwMenuItem): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(item)) {
    if (k.startsWith('data-')) out[k] = (item as Record<string, unknown>)[k]
  }
  return out
}

/** 공유 메뉴 설정(렌더 트리 전체가 참조). */
interface MenuCtx {
  mode: MenuMode
  selectedKeys: string[]
  openKeys: string[]
  triggerSubMenuAction: 'click' | 'hover'
  inlineIndent: number
  onItemClick: (item: OmwMenuItem, e: ReactMouseEvent<HTMLElement>) => void
  onInlineToggle: (key: string) => void
  /** flyout 팝업 서브메뉴 열림/닫힘 → 메뉴 레벨 onOpenChange(열린 키 목록) 통지. 실측: MailToolbar overflow 측정. */
  onPopupOpenChange: (key: string, open: boolean) => void
  // roving 키보드(inline/vertical): Tab 진입점 키 / 항목 DOM 등록 / 포커스 시 활성 갱신.
  anchorKey: string | null
  registerEl: (key: string, el: HTMLElement | null) => void
  setActiveKey: (key: string) => void
}

/** 항목 라벨/아이콘(leaf·submenu-title 공용). */
function renderLabelContent(item: OmwMenuItem): ReactNode {
  // ariaLabel 이 명시된 항목은 항목 요소에 aria-label 이 부여되므로 아이콘은 장식으로 간주해
  // 접근명 계산에서 제외한다(인라인 SVG 의 파일명 `<title>` 등이 이름을 오염시키지 않도록).
  return (
    <>
      {item.icon != null && (
        <span className="om-menu-item-icon" aria-hidden={item.ariaLabel ? true : undefined}>
          {item.icon}
        </span>
      )}
      <span className="om-menu-title-content">{item.label}</span>
    </>
  )
}

function LeafItem({
  item,
  ctx,
  level,
  overflowItem,
  liRef,
  extraStyle,
}: {
  item: OmwMenuItem
  ctx: MenuCtx
  level: number
  overflowItem?: boolean
  liRef?: (el: HTMLLIElement | null) => void
  extraStyle?: CSSProperties
}) {
  const key = item.key ?? ''
  const selected = ctx.selectedKeys.includes(key)
  // 인라인 들여쓰기: depth(level) × inlineIndent. 단, 값이 0(최상위 level 0)이면 inline
  // paddingLeft 를 넣지 않는다 — 넣으면 소비처 SCSS 의 좌측 padding 을 0 으로 덮어써(inline
  // style 우선) 메뉴가 어긋난다. antd 도 collapsed 시 vertical 로 바뀌어 inline 들여쓰기를
  // 적용하지 않으므로(=소비처 padding 유지) 동일 동작.
  const indent = ctx.mode === 'inline' ? ctx.inlineIndent * level : 0
  const inlineStyle: CSSProperties = indent > 0 ? { paddingLeft: indent } : {}
  const setNodeRef = (el: HTMLLIElement | null) => {
    liRef?.(el)
    ctx.registerEl(key, el)
  }
  return (
    <li
      ref={setNodeRef}
      role="menuitem"
      aria-disabled={item.disabled || undefined}
      aria-label={item.ariaLabel || undefined}
      tabIndex={item.disabled ? undefined : ctx.anchorKey === key ? 0 : -1}
      title={item.title}
      style={{ ...inlineStyle, ...item.style, ...extraStyle }}
      className={cx(
        'om-menu-item',
        overflowItem && 'om-menu-overflow-item',
        selected && 'om-menu-item-selected',
        item.disabled && 'om-menu-item-disabled',
        item.danger && 'om-menu-item-danger',
        item.className,
      )}
      onClick={(e) => {
        if (item.disabled) return
        ctx.onItemClick(item, e)
      }}
      onFocus={() => ctx.setActiveKey(key)}
      onMouseEnter={(e) => item.onMouseEnter?.({ key, domEvent: e })}
      onMouseLeave={(e) => item.onMouseLeave?.({ key, domEvent: e })}
      {...pickDataAttrs(item)}
    >
      {renderLabelContent(item)}
    </li>
  )
}

/** inline 확장 서브메뉴(openKeys 제어). blurHandler 가 children[0](title) 클릭 → 토글닫힘. */
function InlineSubMenu({ item, ctx, level }: { item: OmwMenuItem; ctx: MenuCtx; level: number }) {
  const key = item.key ?? ''
  const open = ctx.openKeys.includes(key)
  const selected = ctx.selectedKeys.includes(key)
  return (
    <li
      // 실제 menuitem 은 내부 title div → 래퍼 li 는 presentation(role=menu 하위 bare listitem 위반 방지).
      role="none"
      className={cx(
        'om-menu-submenu',
        'om-menu-submenu-inline',
        'om-menu-submenu',
        open && 'om-menu-submenu-open',
        selected && 'om-menu-submenu-selected',
        item.className,
      )}
      style={item.style}
    >
      <div
        ref={(el) => ctx.registerEl(key, el)}
        role="menuitem"
        aria-expanded={open}
        aria-label={item.ariaLabel || undefined}
        tabIndex={ctx.anchorKey === key ? 0 : -1}
        title={item.title}
        className="om-menu-submenu-title"
        // 최상위(level 0)면 inline paddingLeft 를 생략해 소비처 SCSS padding 을 보존
        // (0 을 넣으면 SCSS 좌측 padding 을 덮어써 어긋남). LeafItem 과 동일 규약.
        style={ctx.inlineIndent * level > 0 ? { paddingLeft: ctx.inlineIndent * level } : undefined}
        onClick={() => ctx.onInlineToggle(key)}
        onFocus={() => ctx.setActiveKey(key)}
        onMouseEnter={(e) => item.onMouseEnter?.({ key, domEvent: e })}
        onMouseLeave={(e) => item.onMouseLeave?.({ key, domEvent: e })}
      >
        {renderLabelContent(item)}
        <i className="om-menu-submenu-arrow" aria-hidden="true" />
      </div>
      {open && (
        <ul className="om-menu om-menu-sub om-menu-inline om-menu om-menu-sub" role="menu">
          {item.children != null && renderItems(item.children, ctx, level + 1)}
        </ul>
      )}
    </li>
  )
}

/** vertical/horizontal flyout 서브메뉴(Radix Popover 팝업). blurHandler title 클릭 → 토글닫힘. */
function PopupSubMenu({
  item,
  ctx,
  level,
  rootHorizontal,
  liRef,
  overflowItem,
  isRest,
  extraStyle,
}: {
  item: OmwMenuItem
  ctx: MenuCtx
  level: number
  rootHorizontal?: boolean
  liRef?: (el: HTMLLIElement | null) => void
  overflowItem?: boolean
  isRest?: boolean
  extraStyle?: CSSProperties
}) {
  const key = item.key ?? ''
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selected = ctx.selectedKeys.includes(key)
  const isHover = ctx.triggerSubMenuAction === 'hover'
  // 최상위 horizontal 서브메뉴는 아래로, 그 외(중첩/vertical)는 오른쪽으로 flyout.
  const side = rootHorizontal ? 'bottom' : 'right'

  // 열림 상태 변경 → 로컬 state + 메뉴 레벨 onOpenChange 통지(MailToolbar overflow 측정 등).
  const changeOpen = (next: boolean) => {
    setOpen(next)
    ctx.onPopupOpenChange(key, next)
  }

  const clearTimer = () => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openNow = () => {
    clearTimer()
    changeOpen(true)
  }
  const closeSoon = () => {
    clearTimer()
    closeTimer.current = setTimeout(() => changeOpen(false), 120)
  }
  // 언마운트 시 대기 중인 close 타이머 정리(CLAUDE.md: cleanup 없는 setTimeout 금지)
  useEffect(() => () => clearTimer(), [])
  const hoverProps = isHover ? { onMouseEnter: openNow, onMouseLeave: closeSoon } : {}

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={changeOpen}>
      <li
        ref={liRef}
        // 실제 menuitem 은 내부 title div → 래퍼 li 는 presentation(role=menu 하위 bare listitem 위반 방지).
        role="none"
        title={item.title}
        className={cx(
          'om-menu-submenu',
          rootHorizontal ? 'om-menu-submenu-horizontal' : 'om-menu-submenu-vertical',
          'om-menu-submenu',
          overflowItem && 'om-menu-overflow-item',
          isRest && 'om-menu-overflow-item-rest',
          open && 'om-menu-submenu-open',
          selected && 'om-menu-submenu-selected',
          item.className,
        )}
        style={{ ...item.style, ...extraStyle }}
        {...hoverProps}
      >
        <PopoverPrimitive.Anchor asChild>
          <div
            ref={(el) => ctx.registerEl(key, el)}
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={item.ariaLabel || undefined}
            tabIndex={ctx.anchorKey === key ? 0 : -1}
            className="om-menu-submenu-title"
            // 클릭 토글(blurHandler 의 title 클릭 닫힘 + triggerSubMenuAction='click' 모두 충족)
            onClick={() => changeOpen(!open)}
            onFocus={() => ctx.setActiveKey(key)}
          >
            {renderLabelContent(item)}
            <i className="om-menu-submenu-arrow" aria-hidden="true" />
          </div>
        </PopoverPrimitive.Anchor>
      </li>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cx('om-menu-submenu', 'om-menu-submenu-popup', item.popupClassName)}
          side={side}
          align="start"
          sideOffset={item.popupOffset ? item.popupOffset[rootHorizontal ? 1 : 0] : 4}
          alignOffset={item.popupOffset ? item.popupOffset[rootHorizontal ? 0 : 1] : 0}
          onOpenAutoFocus={(e) => e.preventDefault()}
          {...(isHover ? { onMouseEnter: openNow, onMouseLeave: closeSoon } : {})}
        >
          <ul className="om-menu om-menu-sub om-menu-vertical om-menu om-menu-sub" role="menu">
            {item.children != null && renderItems(item.children, ctx, level + 1)}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

/** 항목 배열 렌더(재귀). mode 에 따라 submenu 를 inline 확장 vs flyout 팝업으로. */
function renderItems(items: OmwMenuItem[], ctx: MenuCtx, level: number): ReactNode {
  return items.map((item, index) => {
    const key = item.key ?? `__idx_${level}_${index}`
    if (item.type === 'divider') {
      return <li key={key} role="separator" className="om-menu-item-divider" />
    }
    if (item.type === 'group') {
      return (
        <li key={key} role="presentation" className="om-menu-item-group">
          <div className="om-menu-item-group-title">{item.label}</div>
          <ul className="om-menu-item-group-list" role="group">
            {item.children != null && renderItems(item.children, ctx, level)}
          </ul>
        </li>
      )
    }
    if (item.children != null) {
      if (ctx.mode === 'inline') {
        return <InlineSubMenu key={key} item={item} ctx={ctx} level={level} />
      }
      return <PopupSubMenu key={key} item={item} ctx={ctx} level={level} />
    }
    return <LeafItem key={key} item={item} ctx={ctx} level={level} />
  })
}

/** horizontal rc-overflow — 넘친 항목을 rest 서브메뉴로 접음. absolute-hidden 측정으로 재계산. */
function HorizontalMenu({
  items,
  ctx,
  className,
  style,
  overflowedIndicatorPopupClassName,
  menuRef,
}: {
  items: OmwMenuItem[]
  ctx: MenuCtx
  className?: string
  style?: CSSProperties
  overflowedIndicatorPopupClassName?: string
  menuRef?: ForwardedRef<OmwMenuRef>
}) {
  const containerRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<Array<HTMLLIElement | null>>([])
  const restElRef = useRef<HTMLLIElement | null>(null)
  const [displayCount, setDisplayCount] = useState(items.length)

  useImperativeHandle(
    menuRef,
    () => ({
      getOverflowRestElement: () =>
        containerRef.current?.querySelector<HTMLElement>(
          '.om-menu-overflow-item-rest.om-menu-submenu',
        ) ?? null,
    }),
    [],
  )

  const recompute = useCallback(() => {
    const container = containerRef.current
    if (container == null) return
    const avail = container.clientWidth
    const widths = itemRefs.current.map((el) => (el ? el.getBoundingClientRect().width : 0))
    const total = widths.reduce((a, b) => a + b, 0)
    if (total <= avail + 0.5) {
      setDisplayCount(widths.length)
      return
    }
    const restW = restElRef.current ? restElRef.current.getBoundingClientRect().width : 40
    let acc = 0
    let count = 0
    for (let i = 0; i < widths.length; i += 1) {
      const w = widths[i] ?? 0
      if (acc + w + restW <= avail) {
        acc += w
        count += 1
      } else {
        break
      }
    }
    setDisplayCount(count)
  }, [])

  useLayoutEffect(() => {
    recompute()
  }, [recompute, items])

  useEffect(() => {
    const container = containerRef.current
    if (container == null) return undefined
    const ro = new ResizeObserver(() => recompute())
    ro.observe(container)
    return () => ro.disconnect()
  }, [recompute])

  const hasOverflow = displayCount < items.length
  const hiddenItems = items.slice(displayCount)
  const restItem: OmwMenuItem = {
    key: '__rest__',
    label: <span className="om-menu-overflow-item-rest-label">&#183;&#183;&#183;</span>,
    popupClassName: overflowedIndicatorPopupClassName,
    children: hiddenItems,
  }

  return (
    <ul
      ref={containerRef}
      role="menu"
      className={cx(
        'om-menu',
        'om-menu-root',
        'om-menu-horizontal',
        'om-menu-overflow',
        'om-menu',
        'om-menu-horizontal',
        className,
      )}
      style={style}
    >
      {items.map((item, index) => {
        const key = item.key ?? `__idx_0_${index}`
        const hidden = index >= displayCount
        // 측정 가능하도록 항상 렌더(넘친 항목은 absolute-hidden). rest 팝업에도 별도 렌더됨(antd 동일).
        const extraStyle: CSSProperties | undefined = hidden
          ? { position: 'absolute', visibility: 'hidden', pointerEvents: 'none', top: 0 }
          : undefined
        const setRef = (el: HTMLLIElement | null) => {
          itemRefs.current[index] = el
        }
        if (item.type === 'divider') {
          return (
            <li
              key={key}
              ref={setRef}
              role="separator"
              className="om-menu-item-divider om-menu-overflow-item om-menu-item-divider om-menu-overflow-item"
              style={extraStyle}
            />
          )
        }
        if (item.children != null) {
          return (
            <PopupSubMenu
              key={key}
              item={item}
              ctx={ctx}
              level={1}
              rootHorizontal
              overflowItem
              liRef={setRef}
              extraStyle={extraStyle}
            />
          )
        }
        return (
          <LeafItem
            key={key}
            item={item}
            ctx={ctx}
            level={0}
            overflowItem
            liRef={setRef}
            extraStyle={extraStyle}
          />
        )
      })}
      {hasOverflow && (
        <PopupSubMenu
          item={restItem}
          ctx={ctx}
          level={1}
          rootHorizontal
          overflowItem
          isRest
          liRef={(el) => {
            restElRef.current = el
          }}
        />
      )}
    </ul>
  )
}

export function PlainMenu(props: OmwMenuProps & { menuRef?: ForwardedRef<OmwMenuRef> }) {
  const {
    items,
    mode = 'vertical',
    selectedKeys,
    openKeys,
    onOpenChange,
    onClick,
    selectable = true,
    inlineIndent = 24,
    overflowedIndicatorPopupClassName,
    triggerSubMenuAction = 'hover',
    className,
    style,
    menuRef,
  } = props

  // 비제어 selectable 메뉴의 내부 선택 상태(selectedKeys 미지정 시 antd 처럼 클릭 선택).
  const [innerSelected, setInnerSelected] = useState<string[]>([])
  const effectiveSelectedKeys = selectedKeys ?? (selectable ? innerSelected : [])

  const handleItemClick = useCallback(
    (item: OmwMenuItem, e: ReactMouseEvent<HTMLElement>) => {
      const key = item.key ?? ''
      const info: OmwMenuClickInfo = { key, domEvent: e }
      item.onClick?.(info)
      onClick?.(info)
      if (selectable && selectedKeys == null) setInnerSelected([key])
    },
    [onClick, selectable, selectedKeys],
  )

  const handleInlineToggle = useCallback(
    (key: string) => {
      const curr = openKeys ?? []
      const next = curr.includes(key) ? curr.filter((k) => k !== key) : [...curr, key]
      onOpenChange?.(next)
    },
    [openKeys, onOpenChange],
  )

  // flyout 팝업 서브메뉴(vertical/horizontal, overflow rest)의 열린 키 집계 → 메뉴 레벨 onOpenChange 통지.
  const openPopupKeysRef = useRef<string[]>([])
  const handlePopupOpenChange = useCallback(
    (key: string, open: boolean) => {
      const prev = openPopupKeysRef.current
      const next = open
        ? prev.includes(key)
          ? prev
          : [...prev, key]
        : prev.filter((k) => k !== key)
      openPopupKeysRef.current = next
      onOpenChange?.(next)
    },
    [onOpenChange],
  )

  // roving 키보드(inline/vertical). horizontal(overflow) 은 hidden 측정 항목 포커스 문제로 미적용.
  const roving = useMenuRoving(items, mode, openKeys ?? [], handleInlineToggle)
  const isHorizontal = mode === 'horizontal'

  const ctx: MenuCtx = {
    mode,
    selectedKeys: effectiveSelectedKeys,
    openKeys: openKeys ?? [],
    triggerSubMenuAction,
    inlineIndent,
    onItemClick: handleItemClick,
    onInlineToggle: handleInlineToggle,
    onPopupOpenChange: handlePopupOpenChange,
    anchorKey: isHorizontal ? null : roving.anchorKey,
    registerEl: isHorizontal ? noopRegisterEl : roving.registerEl,
    setActiveKey: isHorizontal ? noopSetActiveKey : roving.setActiveKey,
  }

  if (mode === 'horizontal') {
    return (
      <HorizontalMenu
        items={items}
        ctx={ctx}
        className={className}
        style={style}
        overflowedIndicatorPopupClassName={overflowedIndicatorPopupClassName}
        menuRef={menuRef}
      />
    )
  }

  return (
    <ul
      role="menu"
      aria-orientation="vertical"
      className={cx(
        'om-menu',
        'om-menu-root',
        'om-menu',
        mode === 'inline' ? 'om-menu-inline' : 'om-menu-vertical',
        className,
      )}
      style={style}
      onKeyDown={roving.onKeyDown}
    >
      {renderItems(items, ctx, 0)}
    </ul>
  )
}
